import type { MarketVariants } from '@/lib/i18n'

export type Slide8CreditLetterCopy = {
  /** Bank name shown in the letterhead. On the Qatar face the component adds
   *  a neutral bank mark (a Landmark icon) next to it, never a real brand. */
  bankName: string
  /** Whether to render a bank logo image (true) or text wordmark (false).
   *  Kept for source compatibility with the GranGuard fork; no face currently
   *  ships a logo asset. */
  useLogo: boolean
  bankSubtitle: string
  addressLine1: string
  addressLine2: string
  /** Letter date, format conventions: US "April 3, 2026", GB "3 April 2026". */
  letterDate: string
  refLine: string
  privateLine1: string
  privateLine2: string
  /** Includes its trailing comma so Arabic can use "،". */
  salutation: string
  bodyOpening: string
  /** Body sentence that ends with the bolded phone number. Component splices
   *  bodyBefore + (bold)fakePhone + bodyAfter together. */
  bodyBefore: string
  fakePhone: string
  bodyAfter: string

  whatTheyWantLabel: string
  whatTheyWant: string
  saferMoveLabel: string
  saferMove: string
  verifyRoute1: string
  verifyRoute2: string
  warning: string
}

export const copy: MarketVariants<Slide8CreditLetterCopy> = {
  US: {
    bankName: 'Wells Fargo',
    useLogo: false,
    bankSubtitle: 'Personal Banking',
    addressLine1: 'Customer Service Center',
    addressLine2: '420 Montgomery Street, San Francisco CA 94104',
    letterDate: 'April 3, 2026',
    refLine: 'Ref: WF-ACC-7291-04',
    privateLine1: 'Private & confidential',
    privateLine2: 'For the addressee only',
    salutation: 'Dear Cardholder,',
    bodyOpening:
      'We are writing to confirm that a new credit account has been opened in your name.',
    bodyBefore:
      'If you did not authorize this application, please contact our Fraud Prevention Helpline immediately on ',
    fakePhone: '1-800-960-2741',
    bodyAfter: '.',

    whatTheyWantLabel: 'What they want:',
    whatTheyWant: 'Panic and call the number on the letter.',
    saferMoveLabel: 'The safer move:',
    saferMove: 'Step back and verify through a route you found yourself:',
    verifyRoute1: "Look up the bank's real phone number online and call them",
    verifyRoute2:
      'Check your credit report for free at AnnualCreditReport.com (Experian, Equifax, or TransUnion)',
    warning:
      'Even official-looking letters can be fake. Never trust a contact number just because it appears on headed paper.',
  },
  GB: {
    bankName: 'Monzo',
    useLogo: true,
    addressLine1: 'Meridian House',
    addressLine2: '14 Bishopsgate, London EC2N',
    letterDate: '3 April 2026',
    refLine: 'Ref: MZ-ACC-7291-04',
    bodyBefore:
      'If you did not authorise this application, please contact our Fraud Prevention Helpline immediately on ',
    fakePhone: '01632 960274',
    verifyRoute2:
      'Check your credit report for free (ClearScore, Experian, or Credit Karma)',
  },
  CA: {
    bankName: 'TD Canada Trust',
    useLogo: false,
    addressLine1: 'Customer Service Centre',
    addressLine2: '66 Wellington Street West, Toronto ON M5K 1A2',
    letterDate: 'April 3, 2026',
    refLine: 'Ref: TD-ACC-7291-04',
    fakePhone: '1-800-960-2741',
    verifyRoute2:
      'Check your credit report for free with Equifax Canada or TransUnion Canada',
  },
  AU: {
    bankName: 'Commonwealth Bank',
    useLogo: false,
    addressLine1: 'Customer Service Centre',
    addressLine2: 'Tower 1, 201 Sussex Street, Sydney NSW 2000',
    letterDate: '3 April 2026',
    refLine: 'Ref: CBA-ACC-7291-04',
    fakePhone: '1800 960 274',
    verifyRoute2:
      'Check your credit report for free with Equifax, Experian, or illion',
  },
  // Qatar: no real bank named (a neutral bank mark is rendered instead),
  // Doha letterhead, 8-digit local number, Qatar Credit Bureau for the
  // credit-report route. "Center" becomes "Centre" at render.
  QA: {
    bankName: 'Your Bank',
    useLogo: false,
    addressLine2: 'PO Box 1234, West Bay, Doha, Qatar',
    letterDate: '3 April 2026',
    refLine: 'Ref: YB-ACC-7291-04',
    fakePhone: '4496 0274',
    verifyRoute2:
      'Check your credit report with the Qatar Credit Bureau, run by Qatar Central Bank (QCB)',
  },
  ar: {
    bankName: 'بنكك',
    useLogo: false,
    bankSubtitle: 'الخدمات المصرفية للأفراد',
    addressLine1: 'مركز خدمة العملاء',
    addressLine2: 'ص.ب. 1234، الخليج الغربي، الدوحة، قطر',
    letterDate: '3 أبريل 2026',
    refLine: 'المرجع: YB-ACC-7291-04',
    privateLine1: 'خاص وسرّي',
    privateLine2: 'للمرسل إليه فقط',
    salutation: 'عزيزي حامل البطاقة،',
    bodyOpening:
      'نكتب إليك لتأكيد فتح حساب ائتماني جديد باسمك.',
    bodyBefore:
      'إذا لم تكن قد قدّمت هذا الطلب، فيرجى الاتصال فوراً بخط مكافحة الاحتيال على الرقم ',
    fakePhone: '4496 0274',
    bodyAfter: '.',

    whatTheyWantLabel: 'ما يريدونه:',
    whatTheyWant: 'أن تصاب بالذعر وتتصل بالرقم المذكور في الرسالة.',
    saferMoveLabel: 'الخيار الأكثر أماناً:',
    saferMove: 'تراجع خطوة وتحقّق عبر طريق وجدته بنفسك:',
    verifyRoute1: 'ابحث عن رقم البنك الحقيقي عبر الإنترنت واتصل به',
    verifyRoute2:
      'راجع تقريرك الائتماني لدى مركز قطر للمعلومات الائتمانية التابع لمصرف قطر المركزي',
    warning:
      'حتى الرسائل التي تبدو رسمية قد تكون مزيّفة. لا تثق برقم اتصال لمجرد أنه مطبوع على ورق يحمل ترويسة.',
  },
}
